import React, { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import _ from "lodash";

import TinyMCE from 'react-tinymce/lib/components/TinyMCE';
import CollectionWidgetManager from "src/utils/collections.widget.service";

import { animated, useTransition } from "@react-spring/web";
import { DEFAULT_FONTS } from "src/constants/fonts";
import { ReadMoreHtml } from "src/components/readMore";

import styles from './description.module.scss';
import contentStyle from './content.scss';
import { MenuState } from "src/types/settings/accountManagement";
import DealRoomWidgetManager from "src/utils/dealRoom.widget.service";

interface DescriptionEditorProps {
  editorContent: any,
  widgetManager: CollectionWidgetManager | DealRoomWidgetManager,
  maxHeight?: string,
  menuState: MenuState,
  // eslint-disable-next-line no-unused-vars
  onChange: (content: string) => void
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void
}

 const DescriptionEditor: React.FC<DescriptionEditorProps> = ({ editorContent, widgetManager, maxHeight, menuState, onMenuChange, onChange }) => {
  const editorRef = useRef<any>(null);
  const [initialized, setInitialized] = React.useState(false);

  const account = useSelector((state: any) => _.get(state, 'accounts.item'));

  const customfontStyle = _.get(account, 'theme.customStyleSheet', '');

  const validVariables = useMemo(() => {
    const excludedVariables = ["company.name", "signature.description"];
    return widgetManager.variables().filter(item => !excludedVariables.includes(item.id)).map((item) => {
      return item.id;
    });

  }, []);

  const bgColor = _.get(account, 'theme.primaryColor') || "#E51058";
  const buttonTextColor = _.get('account', 'theme.buttonTextColor') || "#FFFFFF";

  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const fonts = useMemo(() => {
    let fonts = [...DEFAULT_FONTS];
    const customFonts = _.get(account, 'theme.fonts', []);

    if (customFonts && customFonts.length > 0) {
      fonts = [...customFonts, ...fonts];
    }

    return fonts.map(font => {
      return `${font.name}=${font.fontFamily}`
    }).join(';');
  }, [account]);

  const notifyParent = (editor: any) => {
    if (!editor) return;
    const template = widgetManager.getRawTemplate(editor.getContent());
    if (template !== editorContent) {
      onChangeRef.current(template);
    }
  };

  useEffect(() => {
    if (menuState.secondaryAction === 'rename') {
      onMenuChange({
        ...menuState, secondaryAction: null
      });

      if (editorRef.current) {
        editorRef.current.focus();
      }
    }
  }, [menuState.secondaryAction]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.getElement() !== null) {
      editorRef.current.getElement().style.maxHeight = maxHeight;
    }
  }, [maxHeight, initialized]);

  const handleChange = (e) => {
    const template = widgetManager.getRawTemplate(e.target.getContent());
    onChange(template);
  }

  const editor = useMemo(() => {
    return editorContent || "";
  }, []);
  
  return (
    <div className={styles.editor}>
      {!initialized &&
        <style>
          {`
            .tox-tinymce-inline {
              display: none !important;
              opacity: 0;
            }
          `}
        </style>
      }

      <TinyMCE
        className={styles.tinyEditor}
        content={editor}
        config={{
          placeholder: "Type your description here...",
          inline: true,
          branding: false,
          statusbar: false,
          contextmenu: false,
          theme: 'silver',
          plugins: 'code, autoresize, noneditable, variable, paste, link, autolink, lists',
          menubar: false,
          toolbar: 'undo redo | bold italic | alignleft aligncenter alignright | fontselect | fontsizeselect | forecolor backcolor | code | link | cta | variables',
          fontsize_formats: '5px 5.5px 6.5px 7.5px 8px 9px 10px 10.5px 11px 12px 14px 16px 18px 20px 22px 24px 26px 28px 36px 48px 72px',
          autoresize_bottom_margin: 0,
          autoresize_min_height: 100,
          paste_retain_style_properties: "color",
          resize: false,
          min_height: 100,
          content_css: [
            'https://fonts.googleapis.com/css?family=Montserrat:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i',
            contentStyle, customfontStyle
          ],
          content_style: `
          h1, h2, h3, h4, h5, h6, p {
          color: #000;
          margin: 0.5rem 1rem;
          word-break: break-word;
          }
          `,
          font_formats: fonts,
          default_link_target: "_blank",
          browser_spellcheck: true,
          convert_urls: false,
          relative_urls: false,
          remove_script_host: false,
          variable_mapper: (id) => {
            const variable = widgetManager.variables().find((item) => {
              return item.id == id;
            });

            if (variable) {
              return variable.name;
            }
          },
          variable_valid: validVariables,

          setup: (editor) => {
            editorRef.current = editor;

            const immediateEvents = ['Input', 'KeyUp', 'ExecCommand', 'SetContent', 'Undo', 'Redo'];
            immediateEvents.forEach(ev => editor.on(ev as any, () => notifyParent(editor)));

            editor.on('PostRender', () => {
              setTimeout(() => {
                const el = editor.getElement();
                if (el) {
                  el.style.maxHeight = maxHeight;
                }
                editor.ui?.hide?.();
                setInitialized(true);
              }, 1000);
            });
            
            editor.ui.registry.addButton("cta", {
              text: "CTA Button",
              onAction: () => {
                editor.insertContent(`<a href="#" class="collection_cta_button" style="outline: none; background: ${bgColor}; color: ${buttonTextColor}; padding: 10px 15px;">CLICK HERE</a>`)
                notifyParent(editor);
              }
            });

            editor.ui.registry.addContextToolbar('ctaToolBar', {
              predicate: function (node) {
                return node.className === 'collection_cta_button'
              },
              items: 'forecolor backcolor link',
              position: 'line',
              scope: 'node'
            });

            editor.ui.registry.addButton('placeholder_label', {
              text: 'Placeholders: ',
              onAction: () => {

              },
            });

            editor.ui.registry.addMenuButton('variables', {
              text: 'Variables',
              fetch: (callback) => {
                const items: any[] = [];
                widgetManager.variables().forEach((variable) => {
                  if (_.includes(validVariables, variable.id)) {
                    items.push({
                      type: 'menuitem',
                      text: variable.name,
                      onAction: () => {
                        editor.plugins.variable.addVariable(variable.id);
                        notifyParent(editor);
                      }
                    });
                  }
                });

                callback(items);
              }
            });

            editor.on('click', (e) => {
              const node = e.target;
              if (node.className === "collection_cta_button") {
                editor.selection.select(node, true);
              }
            });
          }
        }}
        onChange={handleChange}
      />
    </div>
  );
}

export default DescriptionEditor;